import { Button, Card, Inline, Stack } from '@omnipresentgroup/design-system';
import {
  useXeroConnectionUpdateMutation,
  useXeroOrganisations,
} from '@omnipresentgroup/domains';
import { Form } from '@omnipresentgroup/flexible-forms';
import { useState } from 'react';

import { useManagerUser } from '../../hooks';

const FORM_ID = 'xero-organisation-selection-form';

type Props = {
  onOrganizationSelection?: () => void;
};

export const XeroOrganisationSelection = ({
  onOrganizationSelection,
}: Props) => {
  const { companyId } = useManagerUser();

  const { data: organisations } = useXeroOrganisations({ clientId: companyId });

  const { mutate: updateXeroConnection, isPending: isXeroUpdating } =
    useXeroConnectionUpdateMutation({
      clientId: companyId,
      onSuccess: () => {
        onOrganizationSelection?.();
      },
    });

  const [selectedOrganisation, setSelectedOrganisation] = useState<string>();

  return (
    <Card p="32" maxW={600} w="stretch">
      <Stack gap="32">
        <Form
          id={FORM_ID}
          formData={{
            organisation: selectedOrganisation,
          }}
          onChange={({ formData }) => {
            setSelectedOrganisation(formData?.organisation);
          }}
          schema={{
            type: 'object',
            description:
              'Please select the appropriate Xero organisation from the list, as multiple organisations are mapped to your account. This step is essential to ensure accurate data synchronization.',
            properties: {
              organisation: {
                title: 'Organisation',
                type: 'string',
                oneOf: organisations?.map((organisation) => ({
                  const: organisation.id,
                  title: organisation.name,
                })) ?? [
                  {
                    const: 'no-organisations',
                    title: 'No organisations found',
                  },
                ],
              },
            },
          }}
          uiSchema={{
            'ui:submitButtonOptions': {
              norender: true,
            },
            organisation: {
              'ui:placeholder': 'Select an organisation',
              'ui:options': {
                isClearable: true,
              },
            },
          }}
        />
        <Inline justify="flex-end">
          <Button
            type="submit"
            form={FORM_ID}
            disabled={!organisations?.length || !selectedOrganisation}
            loading={isXeroUpdating}
            onClick={() =>
              selectedOrganisation &&
              updateXeroConnection({
                xeroOrganisation: {
                  id: selectedOrganisation,
                },
              })
            }
          >
            Submit
          </Button>
        </Inline>
      </Stack>
    </Card>
  );
};
